import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import Header from "./components/Header";
import Cursor from "./components/Cursor";
import Grain from "./components/Grain";
import ProgressBar from "./components/ProgressBar";
import Footer from "./sections/Footer";
import Home from "./pages/Home";
import Analisi from "./pages/Analisi";
import { resetMeta } from "./lib/seo";
import { ImageViewerProvider } from "./components/viewer/ImageViewerProvider";
import { getAnchorPosition } from "./lib/scroll";
import { ConsentProvider } from "./components/consent/ConsentProvider";
import "@/App.css";

gsap.registerPlugin(ScrollTrigger);

// pagine secondarie caricate solo quando servono (bundle iniziale più leggero)
const Privacy = lazy(() => import("./pages/Privacy"));
const QuaderniArchivio = lazy(() => import("./pages/QuaderniArchivio"));
const Quaderno = lazy(() => import("./pages/Quaderno"));
const Scrivi = lazy(() => import("./pages/Scrivi"));
const Grazie = lazy(() => import("./pages/Grazie"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="eb" data-testid="error-boundary">
                    <div className="container">
                        <p className="h2">Errore.</p>
                        <p className="caption" style={{ marginTop: 16 }}>
                            Ricaricate la pagina.
                        </p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function PageMeta() {
    const { pathname } = useLocation();
    useEffect(() => { resetMeta(); }, [pathname]);
    return null;
}

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
        if (hash) {
            const id = hash.slice(1);
            // salto immediato dopo che il pin dell'hero ha ricalcolato l'altezza della pagina
            const jump = () => {
                ScrollTrigger.refresh();
                if (window.__lenis) window.__lenis.resize();
                const y = getAnchorPosition(id);
                if (y === null) return;
                if (window.__lenis) window.__lenis.scrollTo(y, { immediate: true, force: true });
                else window.scrollTo(0, y);
            };
            const t1 = setTimeout(jump, 350);
            const t2 = setTimeout(jump, 1300);
            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
            };
        }
    }, [pathname, hash]);
    return null;
}

function Chrome() {
    const { pathname } = useLocation();
    if (pathname === "/analisi") return null;
    return (
        <>
            <Cursor />
            <Grain />
            <ProgressBar />
        </>
    );
}

function FooterSwitch() {
    const { pathname } = useLocation();
    return pathname === "/analisi" ? null : <Footer />;
}

function App() {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.08 });
        window.__lenis = lenis;
        lenis.on("scroll", ScrollTrigger.update);
        const raf = (time) => lenis.raf(time * 1000);
        gsap.ticker.add(raf);
        gsap.ticker.lagSmoothing(0);
        if (document.fonts) {
            document.fonts.ready.then(() => ScrollTrigger.refresh());
        }
        return () => {
            gsap.ticker.remove(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <BrowserRouter>
            <ErrorBoundary>
                <ConsentProvider>
                <ImageViewerProvider>
                <ScrollToTop />
                <PageMeta />
                <Chrome />
                <Header />
                <Suspense fallback={<div className="page" aria-busy="true" />}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/cookie" element={<CookiePolicy />} />
                        <Route path="/quaderni" element={<QuaderniArchivio />} />
                        <Route path="/quaderni/:slug" element={<Quaderno />} />
                        <Route path="/scrivi" element={<Scrivi />} />
                        <Route path="/analisi" element={<Analisi />} />
                        <Route path="/grazie" element={<Grazie />} />
                        <Route path="*" element={<Home />} />
                    </Routes>
                </Suspense>
                <FooterSwitch />
                </ImageViewerProvider>
                </ConsentProvider>
            </ErrorBoundary>
        </BrowserRouter>
    );
}

export default App;
